<template>
  <div class="fixed-container">
    <div class="mobile-footer" :class="[store.currentDistrict ? 'expanded' : null]">
      <UIRoundButton v-if="$route.name !== 'game'" icon="home" @click="() => handleButtonClick(null)" />

      <div :class="[store.currentDistrict ? 'full-width-block' : 'left-block']">
        <UIBuildingButton
          icon="mining"
          :text="$t('buildingMiningDistrict')"
          :badge-value="getBadgeValue(Districts.MINING)"
          :mode="store.currentDistrict === Districts.MINING ? 'full' : 'compact'"
          @click.stop="() => handleButtonClick(Districts.MINING)"
        />

        <UIBuildingButton
          icon="produce"
          :text="$t('buildingProductionDistrict')"
          :badge-value="getBadgeValue(Districts.PRODUCTION)"
          :mode="store.currentDistrict === Districts.PRODUCTION ? 'full' : 'compact'"
          @click.stop="() => handleButtonClick(Districts.PRODUCTION)"
        />

        <UIBuildingButton
          icon="city"
          :text="$t('buildingCityDistrict')"
          :badge-value="getBadgeValue(Districts.CITY)"
          :mode="store.currentDistrict === Districts.CITY ? 'full' : 'compact'"
          @click.stop="() => handleButtonClick(Districts.CITY)"
        />

        <UIBuildingButton
          icon="expedition"
          :text="$t('buildingExpeditionDistrict')"
          :badge-value="getBadgeValue(Districts.EXPEDITION)"
          :mode="store.currentDistrict === Districts.EXPEDITION ? 'full' : 'compact'"
          @click.stop="() => handleButtonClick(Districts.EXPEDITION)"
        />

        <UIBuildingButton icon="dex" :mode="'compact'" @click.stop="showDexModalInjected" />
      </div>

      <UIRoundButton
        v-if="!store.currentDistrict && address"
        :badge-value="craftingSocket.parsedData?.length ? craftingSocket.parsedData?.length : ''"
        icon="notifications"
        @click="isNotificationsVisible = !isNotificationsVisible"
      />
      <MobileNotifications :recipes="craftingSocket.parsedData || []" :visible="isNotificationsVisible" />
      <div v-if="isNotificationsVisible" class="notificationsOverlay" @click="isNotificationsVisible = false"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { Districts } from '~/types/districts';
import type { UnclaimedRecipeProps } from '~/types/crafting';
import { path } from '~/utils/constants';
import { WebSocketsKeys } from '~/types/common';

const store = useMainStore();
const { address } = useWeb3ModalAccount();
const { blockchain } = useEnvs();
const isNotificationsVisible = ref(false);
const unclaimedQtys = ref(new Map<Districts, number>());
const { $websockets: websockets } = useNuxtApp();
const craftingSocket = websockets?.[WebSocketsKeys.craftingActivity];

const showDexModalInjected = inject<() => void>('showDexModal');

watch([craftingSocket], () => {
  if (!craftingSocket || !unclaimedQtys.value) return;

  const buildingHashList = blockchain.buildings;
  unclaimedQtys.value.clear();

  craftingSocket.parsedData.forEach((recipe: UnclaimedRecipeProps) => {
    const { buildingHash } = recipe;
    const recipeDistrict = buildingHashList[buildingHash].district;

    const currentUnclaimedValue = unclaimedQtys.value.get(recipeDistrict) || 0;
    unclaimedQtys.value.set(recipeDistrict, currentUnclaimedValue + recipe.unclaimedModifier);
  });
});

const getBadgeValue = (district: Districts): number | undefined => {
  return unclaimedQtys.value.get(district);
};

const handleButtonClick = (district: Districts | null) => {
  store.currentDistrict = district;
  navigateTo(district ? path.public.districts : path.public.game);
};
</script>

<style lang="scss" scoped>
.button-container {
  position: relative;
  height: 50px;
}

.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.5s ease,
    transform 0.5s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-enter-to,
.fade-leave {
  opacity: 1;
  transform: translateY(0);
}

.fixed-container {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 5px;
  z-index: 10;
  overflow-x: auto;
  scrollbar-width: thin;

  @media (max-width: 376px) {
    padding: 10px 5px;
  }
}

.left-block {
  display: flex;
}

.full-width-block {
  display: flex;
  justify-content: left;

  > * {
    margin-left: 5px;
  }
}

.left-block > *:not(:first-child) {
  margin-left: 1em;
}

.mobile-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.expanded {
  justify-content: flex-start;
}

.notificationsOverlay {
  position: fixed;
  width: 100vw;
  height: 100dvh;
  background-color: rgba(0, 0, 0, 0.3);
  top: 0;
  left: 0;
}
</style>
